import React from 'react';
import styled from 'styled-components';
import { colors } from '../../../design-system';
import { InstagramIcon } from '../../../design-system/components/data-display/EmailProviderIcons';

interface InstagramPostViewProps {
  url: string;
  title: string;
  description?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 20px;
`;

const PostContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 600px;
  height: 100%;
  max-height: 100%;
  margin: 0 auto;
  
  /* Let the post scale to fit available height while maintaining aspect ratio */
  iframe {
    width: 100%;
    height: 100%;
    max-height: calc(100vh - 100px);
    object-fit: contain;
  }
`;

const PostEmbed = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
  background: white;
`;

const ViewButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: white;
  background: linear-gradient(135deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%);
  padding: 8px 14px;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 500;
  transition: opacity 0.15s ease;
  
  &:hover {
    opacity: 0.9;
  }
`;

export const InstagramPostView: React.FC<InstagramPostViewProps> = ({ url, title, description }) => {
  // Convert URL to embed format
  const getEmbedUrl = () => {
    if (!url) return null;
    
    if (url.includes('/p/') || url.includes('/reel/')) {
      let cleanUrl = url.split('?')[0];
      if (!cleanUrl.endsWith('/')) {
        cleanUrl += '/';
      }
      return `${cleanUrl}embed/captioned`;
    }
    return null;
  };
  
  const embedUrl = getEmbedUrl();
  
  if (!embedUrl) {
    return (
      <Container>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p style={{ color: colors.text.secondary }}>Unable to load Instagram post</p>
          <ViewButton href={url} target="_blank" rel="noopener noreferrer">
            <InstagramIcon size={16} />
            View on Instagram
          </ViewButton>
        </div>
      </Container>
    );
  }
  
  return (
    <Container>
      <PostContainer>
        <PostEmbed
          src={embedUrl}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </PostContainer>
    </Container>
  );
};

